import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { dayInZone } from '@/helpers'
import {
  getORPerformanceTrack,
  getORPerformanceTrackMonthlySummary,
} from '@/services/eos/or-performance-tracker.service'
import { useQuery } from '@tanstack/vue-query'
import { DateTime } from 'luxon'
import { computed, type Ref } from 'vue'

export const useORPerformanceTrackerService = () => {
  const performanceTrackQuery = (
    selectedSite: Ref<ISiteIdentifier | undefined>,
    selectedDate: Ref<Date>,
    selectedTimezone: Ref<string>,
    forceRefresh: Ref<boolean>
  ) =>
    useQuery({
      queryFn: () => {
        const day = dayInZone(selectedDate.value, selectedTimezone.value)
        if (!day) {
          return Promise.reject(new Error('Invalid date for OR performance track query.'))
        }
        const site = selectedSite.value!.site_id! // ensured to be set by enabled condition
        return getORPerformanceTrack({ day, site, force_refresh: forceRefresh.value })
      },
      queryKey: ['or-performance-track', selectedSite, selectedDate, selectedTimezone],
      enabled: computed(() => !!selectedSite.value?.site_id && !!selectedDate.value && !!selectedTimezone.value),
      refetchOnWindowFocus: false,
    })

  const performanceTrackMonthlySummaryQuery = (
    selectedSite: Ref<ISiteIdentifier | undefined>,
    selectedDate: Ref<Date>
  ) =>
    useQuery({
      queryFn: () =>
        getORPerformanceTrackMonthlySummary({
          site: selectedSite.value!.site_id!, // ensured to be set by enabled condition
          month: selectedDate.value.getMonth() + 1,
          year: selectedDate.value.getFullYear(),
        }),
      queryKey: [
        'or-performance-track-monthly',
        selectedSite,
        computed(() => DateTime.fromJSDate(selectedDate.value).toFormat('yyyy-MM')),
      ],
      enabled: computed(() => !!selectedSite.value?.site_id),
      refetchOnWindowFocus: false,
    })

  return {
    performanceTrackQuery,
    performanceTrackMonthlySummaryQuery,
  }
}
